import {
  BadgeCheck,
  Copy,
  Edit2,
  Link as LinkIcon,
  MessageCircle,
  Power,
  RotateCw,
  Share2,
  ShieldOff,
  Trash2,
  type LucideIcon,
} from 'lucide-react';
import { useTranslation } from '@dukanti/shared';
import type { Courier, TranslationKey } from '@dukanti/shared';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

/* v8 ignore next -- optional callback defaults are defensive no-ops. */
const noopCourierHandler = () => {};

interface CourierActionButtonProps {
  readonly label: string;
  readonly disabled: boolean;
  readonly onClick: () => void;
  readonly icon: LucideIcon;
}

function CourierActionButton({
  label,
  disabled,
  onClick,
  icon: Icon,
}: CourierActionButtonProps): React.JSX.Element {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          disabled={disabled}
          onClick={onClick}
          aria-label={label}
          title={label}
        >
          <Icon className="size-4" aria-hidden />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="top">
        <span>{label}</span>
      </TooltipContent>
    </Tooltip>
  );
}

export interface CouriersTableProps {
  readonly couriers: readonly Courier[];
  readonly isMutating?: boolean;
  readonly onEdit: (courier: Courier) => void;
  readonly onToggleActive: (courier: Courier) => void;
  readonly onDelete: (courier: Courier) => void;
  readonly onVerify?: (courier: Courier) => void;
  readonly onCopyPortalLink?: (courier: Courier) => void;
  readonly onSharePortalLink?: (courier: Courier) => void;
  readonly onSendPortalWhatsApp?: (courier: Courier) => void;
  readonly onRevokePortalLink?: (courier: Courier) => void;
  readonly onRegeneratePortalLink?: (courier: Courier) => void;
  readonly onGeneratePortalLink?: (courier: Courier) => void;
  readonly isPortalActionPending?: boolean;
}

export function CouriersTable({
  couriers,
  isMutating = false,
  onEdit,
  onToggleActive,
  onDelete,
  onVerify = noopCourierHandler,
  onCopyPortalLink = noopCourierHandler,
  onSharePortalLink = noopCourierHandler,
  onSendPortalWhatsApp = noopCourierHandler,
  onRevokePortalLink = noopCourierHandler,
  onRegeneratePortalLink = noopCourierHandler,
  onGeneratePortalLink = noopCourierHandler,
  isPortalActionPending = false,
}: CouriersTableProps): React.JSX.Element {
  const { t, dir, lang } = useTranslation();

  const formatDate = (iso: string): string =>
    new Intl.DateTimeFormat(lang, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(new Date(iso));

  return (
    <div
      dir={dir}
      data-testid="couriers-table"
      className="border-border bg-card shadow-admin-card hidden overflow-hidden rounded-lg border md:block"
    >
      <TooltipProvider delayDuration={200}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('admin.couriers.columns.name' as TranslationKey)}</TableHead>
              <TableHead>{t('admin.couriers.columns.phone' as TranslationKey)}</TableHead>
              <TableHead>{t('admin.couriers.columns.status' as TranslationKey)}</TableHead>
              <TableHead>{t('admin.couriers.verification.title' as TranslationKey)}</TableHead>
              <TableHead>{t('admin.couriers.columns.createdAt' as TranslationKey)}</TableHead>
              <TableHead className="text-end">
                {t('admin.couriers.columns.actions' as TranslationKey)}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {couriers.map((courier) => {
              const canUsePortal = courier.active && courier.verification_status === 'approved';
              const hasActivePortalLink = courier.portal_link?.status === 'available';
              const disablePortalActions = isMutating || isPortalActionPending;
              return (
                <TableRow key={courier.id} data-testid="couriers-table-row">
                  <TableCell>
                    <div className="min-w-0">
                      <div className="text-foreground truncate text-sm font-medium">
                        {courier.name}
                      </div>
                      {courier.internal_notes ? (
                        <div className="text-muted-foreground mt-1 line-clamp-1 text-xs">
                          {courier.internal_notes}
                        </div>
                      ) : null}
                    </div>
                  </TableCell>
                  <TableCell dir="ltr" className="text-start text-sm">
                    {courier.phone_number}
                  </TableCell>
                  <TableCell>
                    <Badge variant={courier.active ? 'success' : 'neutral'}>
                      {t(
                        (courier.active
                          ? 'admin.couriers.status.active'
                          : 'admin.couriers.status.inactive') as TranslationKey,
                      )}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={courier.verification_status === 'approved' ? 'success' : 'warning'}
                    >
                      {t(
                        (courier.verification_status === 'approved'
                          ? 'admin.couriers.verification.approved'
                          : 'admin.couriers.verification.pending') as TranslationKey,
                      )}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground whitespace-nowrap text-sm">
                    {formatDate(courier.created_at)}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap justify-end gap-2">
                      {canUsePortal ? (
                        <>
                          {hasActivePortalLink ? (
                            <>
                              <CourierActionButton
                                label={t('admin.couriers.portal.copyLink' as TranslationKey)}
                                disabled={disablePortalActions}
                                onClick={() => onCopyPortalLink(courier)}
                                icon={Copy}
                              />
                              <CourierActionButton
                                label={t('admin.couriers.portal.shareLink' as TranslationKey)}
                                disabled={disablePortalActions}
                                onClick={() => onSharePortalLink(courier)}
                                icon={Share2}
                              />
                              <CourierActionButton
                                label={t('admin.couriers.portal.sendWhatsApp' as TranslationKey)}
                                disabled={disablePortalActions}
                                onClick={() => onSendPortalWhatsApp(courier)}
                                icon={MessageCircle}
                              />
                              <CourierActionButton
                                label={t('admin.couriers.portal.revoke' as TranslationKey)}
                                disabled={disablePortalActions}
                                onClick={() => onRevokePortalLink(courier)}
                                icon={ShieldOff}
                              />
                            </>
                          ) : (
                            <CourierActionButton
                              label={t('admin.couriers.portal.generate' as TranslationKey)}
                              disabled={disablePortalActions}
                              onClick={() => onGeneratePortalLink(courier)}
                              icon={LinkIcon}
                            />
                          )}
                          <CourierActionButton
                            label={t('admin.couriers.portal.regenerate' as TranslationKey)}
                            disabled={disablePortalActions}
                            onClick={() => onRegeneratePortalLink(courier)}
                            icon={RotateCw}
                          />
                        </>
                      ) : null}
                      <CourierActionButton
                        label={t('admin.couriers.verification.title' as TranslationKey)}
                        disabled={isMutating}
                        onClick={() => onVerify(courier)}
                        icon={BadgeCheck}
                      />
                      <CourierActionButton
                        label={t('admin.couriers.actions.edit' as TranslationKey)}
                        disabled={isMutating}
                        onClick={() => onEdit(courier)}
                        icon={Edit2}
                      />
                      <CourierActionButton
                        label={t(
                          (courier.active
                            ? 'admin.couriers.actions.deactivate'
                            : 'admin.couriers.actions.activate') as TranslationKey,
                        )}
                        disabled={isMutating}
                        onClick={() => onToggleActive(courier)}
                        icon={Power}
                      />
                      <CourierActionButton
                        label={t('admin.couriers.actions.delete' as TranslationKey)}
                        disabled={isMutating}
                        onClick={() => onDelete(courier)}
                        icon={Trash2}
                      />
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TooltipProvider>
    </div>
  );
}
